import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Users } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogTitle,
} from '@ncoder/ui';
import { cn } from '@/lib/utils';
import { formatCurrencyUsd } from '@/components/organisms/org-admin/usageFormat';

/**
 * One payment, split across several seats.
 *
 * Inherits every hazard of the single-seat dialog — it charges a card, and the endpoint sends no
 * Stripe idempotency key — so it inherits the same three defences: the submit is disabled while
 * pending, the mutation never retries, and an ambiguous failure is reported as "we don't know" with
 * no retry offered.
 *
 * It adds one of its own. A bulk charge is a single irreversible amount assembled from a list the
 * admin built by hand, so the TOTAL is the figure that has to be impossible to miss — a per-row
 * mistake is invisible if the only number on screen is the one in the row it happened in. The total
 * is therefore restated in the footer, beside the button that spends it.
 *
 * Amounts are per seat rather than one number split evenly. An even split reads as simpler and is
 * the wrong default: the reason to top several people up at once is usually that they need
 * different amounts, and a UI that assumes otherwise forces the admin back to doing them one by one.
 */

export interface BulkTopUpSeat {
  userId: string;
  email: string;
  /**
   * What the member can still spend this cycle, so an assignment can be previewed as the figure the
   * admin is actually trying to change ("$5 → $25"). `null` when the report could not say; the row
   * then previews nothing rather than a number built on a guess. `assign` mode only.
   */
  memberAvailableUsd?: number | null;
}

export interface BulkTopUpDialogProps {
  open: boolean;
  seats: BulkTopUpSeat[];
  /**
   * `charge` takes money now. `assign` spends credit already bought, so it takes none.
   *
   * One component rather than two because the task is identical — pick members, set amounts, see
   * the total — and only the funding source and its constraint differ. Splitting it would duplicate
   * the per-seat validation and the total-restatement, which are the parts that keep this screen
   * from charging the wrong amount.
   */
  mode?: 'charge' | 'assign';
  /** `assign` mode only: the ceiling. Exceeding it is refused server-side, so it is refused here. */
  availableUsd?: number;
  /** Whether a saved card exists — decides whether this charges now or goes via Stripe Checkout. */
  hasPaymentMethod: boolean;
  /** Extra budget per dollar, from `GET /billing`. Never defaulted — see `TopUpDialog`. */
  bonusMultiplier?: number;
  isSubmitting: boolean;
  errorMessage: string | null;
  /** Set when the outcome is unknown: the dialog then refuses to resubmit. */
  isAmbiguous: boolean;
  onSubmit: (allocations: Array<{ user_id: string; amount_usd: number }>) => void;
  onClose: () => void;
}

/** Applied to every newly-selected seat, so a selection is immediately a valid allocation. */
const DEFAULT_AMOUNT = '25';

export function BulkTopUpDialog({
  open,
  seats,
  mode = 'charge',
  availableUsd,
  hasPaymentMethod,
  bonusMultiplier,
  isSubmitting,
  errorMessage,
  isAmbiguous,
  onSubmit,
  onClose,
}: BulkTopUpDialogProps) {
  /** Amount per selected seat, keyed by user id. Absence from this map means "not selected". */
  const [amounts, setAmounts] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    // Nobody is charged by default. The admin has to name every person before the button unlocks —
    // the same rule the single-seat dialog's member picker follows, and for the same reason.
    setAmounts({});
  }, [open]);

  const toggle = (userId: string) => {
    setAmounts((prev) => {
      const next = { ...prev };
      if (userId in next) delete next[userId];
      else next[userId] = DEFAULT_AMOUNT;
      return next;
    });
  };

  const entries = useMemo(
    () =>
      Object.entries(amounts)
        .map(([userId, raw]) => ({ userId, amount: Number(raw) }))
        .filter((entry) => Number.isFinite(entry.amount) && entry.amount > 0),
    [amounts],
  );

  const total = entries.reduce((sum, entry) => sum + entry.amount, 0);
  const hasBonus = typeof bonusMultiplier === 'number' && bonusMultiplier > 1;
  const granted = hasBonus ? total * bonusMultiplier : total;
  const bonusPercent = hasBonus ? Math.round((bonusMultiplier - 1) * 100) : 0;

  const isAssign = mode === 'assign';
  /*
   * In assign mode the bonus was already applied when the credit was BOUGHT, so assigning is 1:1.
   * Showing a bonus here would promise it twice for one payment — the same double-count the server
   * avoids by routing assignment away from the top-up path.
   */
  const showBonus = hasBonus && !isAssign;
  const overBalance =
    isAssign && typeof availableUsd === 'number' && total > availableUsd + 1e-9;

  const selectedCount = Object.keys(amounts).length;
  /*
   * Every SELECTED seat must carry a valid amount, not just some of them. Submitting with one row
   * blank would silently drop that person from a charge the admin believes covers them.
   */
  const allSelectedValid = selectedCount > 0 && entries.length === selectedCount;
  const canSubmit = !isSubmitting && !isAmbiguous && allSelectedValid && !overBalance;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isSubmitting && onClose()}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            {isAssign ? 'Assign credit' : 'Add credit for members'}
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            {isAssign ? (
              <>
                Spend credit you have already bought. No payment is taken.{' '}
                {typeof availableUsd === 'number' && (
                  <>
                    <span className="font-medium text-foreground">
                      {formatCurrencyUsd(availableUsd)}
                    </span>{' '}
                    available.
                  </>
                )}
              </>
            ) : (
              <>
                One payment, split across the members you choose.{' '}
                {hasPaymentMethod
                  ? 'Your saved card will be charged once.'
                  : 'You will be taken to Stripe to pay once.'}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogMain className="space-y-4">
          {/*
            The one sentence a grant dialog owes the person spending money: what happens to it at
            the next reset. The rule is the backend's (#327): credit stays until spent, across
            resets and past a plan ending; only the plan's own allowance resets.
          */}
          {isAssign && (
            <p className="oa-meta">
              Credit you give stays with the member until it is spent — it carries over between cycles
              and is kept if their plan ends. Spend uses the plan allowance first, then credit.
            </p>
          )}
          {showBonus && (
            <p className="oa-meta">
              Every top-up gets{' '}
              <span className="font-semibold text-success-strong">{bonusPercent}% extra credit</span>{' '}
              — applied to each member&rsquo;s share.
            </p>
          )}

          <fieldset>
            <legend className="oa-micro mb-2">Members</legend>
            <div className="max-h-[280px] overflow-y-auto rounded-[var(--oa-radius-md)] border border-border/60">
              {seats.map((seat) => {
                const isSelected = seat.userId in amounts;
                const raw = amounts[seat.userId] ?? '';
                const parsed = Number(raw);
                const invalid = isSelected && (!Number.isFinite(parsed) || parsed <= 0);

                return (
                  <div
                    key={seat.userId}
                    className="flex items-center gap-3 border-b border-border/40 px-3 py-2 last:border-b-0"
                  >
                    <label className="flex min-w-0 flex-1 items-center gap-2.5">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isSubmitting}
                        onChange={() => toggle(seat.userId)}
                        className="h-4 w-4 shrink-0 rounded border-border accent-primary"
                      />
                      <span className="truncate text-[13px]">{seat.email}</span>
                    </label>

                    {/* The amount field appears only for a selected seat: a row of disabled inputs
                        for people who are not being charged is noise on a screen about money. */}
                    {/* The consequence beside the input: the member's available figure after this
                        assignment. Only when the report answered; a dash would be noise on a row
                        whose amount the admin is still typing. */}
                    {isSelected && isAssign && typeof seat.memberAvailableUsd === 'number' && !invalid && (
                      <span className="oa-meta hidden shrink-0 tabular-nums sm:inline">
                        {formatCurrencyUsd(seat.memberAvailableUsd)} → {formatCurrencyUsd(seat.memberAvailableUsd + parsed)}
                      </span>
                    )}
                    {isSelected && (
                      <label className="flex shrink-0 items-center gap-1.5">
                        <span className="sr-only">Amount for {seat.email}</span>
                        <span className="oa-meta">$</span>
                        <input
                          type="number"
                          inputMode="decimal"
                          min={1}
                          value={raw}
                          disabled={isSubmitting}
                          onChange={(event) =>
                            setAmounts((prev) => ({ ...prev, [seat.userId]: event.target.value }))
                          }
                          aria-invalid={invalid || undefined}
                          className={cn(
                            'oa-interactive h-8 w-24 rounded-md border bg-background px-2 text-[13px] tabular-nums',
                            invalid ? 'border-destructive' : 'border-border/70',
                          )}
                        />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          </fieldset>

          {/*
            The total, restated away from the rows that produced it. A mistyped digit in one row is
            invisible when the only place it shows is that row; it is obvious in a total.
          */}
          {selectedCount > 0 && (
            <div className="rounded-[var(--oa-radius-md)] border border-border/60 bg-muted/30 p-3 text-[13px]">
              {isAssign ? (
                <p>
                  Assigning{' '}
                  <span className="font-semibold tabular-nums">{formatCurrencyUsd(total)}</span>{' '}
                  across {selectedCount} member{selectedCount === 1 ? '' : 's'}
                  {typeof availableUsd === 'number' && (
                    <>
                      , leaving{' '}
                      <span className="font-semibold tabular-nums">
                        {formatCurrencyUsd(Math.max(0, availableUsd - total))}
                      </span>{' '}
                      of credit.
                    </>
                  )}
                </p>
              ) : (
                <p>
                  {hasPaymentMethod ? 'Your card will be charged ' : 'You will pay '}
                  <span className="font-semibold tabular-nums">{formatCurrencyUsd(total)}</span> in
                  one payment
                  {hasBonus ? (
                    <>
                      , adding{' '}
                      <span className="font-semibold tabular-nums text-success-strong">
                        {formatCurrencyUsd(granted)}
                      </span>{' '}
                      of budget across {selectedCount} member{selectedCount === 1 ? '' : 's'}.
                    </>
                  ) : (
                    <>
                      , split across {selectedCount} member{selectedCount === 1 ? '' : 's'}.
                    </>
                  )}
                </p>
              )}
              {!allSelectedValid && (
                <p className="mt-1.5 text-[12px] font-medium text-destructive-strong">
                  Every selected member needs an amount greater than zero.
                </p>
              )}
              {/* Refused here as well as server-side: the server is the authority, but letting the
                  admin submit an assignment that cannot succeed wastes a round trip on a screen
                  where they are already counting money. */}
              {overBalance && (
                <p className="mt-1.5 text-[12px] font-medium text-destructive-strong">
                  That is more than the {formatCurrencyUsd(availableUsd!)} of credit available.
                </p>
              )}
            </div>
          )}

          {isAmbiguous ? (
            // No retry offered. The charge may already exist and the endpoint cannot de-duplicate.
            <div
              role="alert"
              className="flex gap-2.5 rounded-[var(--oa-radius-md)] border border-warning/30 bg-warning/[0.07] p-3"
            >
              <AlertTriangle className="mt-px h-4 w-4 shrink-0 text-warning-strong" aria-hidden />
              <p className="text-[13px] leading-snug">{errorMessage}</p>
            </div>
          ) : (
            errorMessage && (
              <p role="alert" className="text-[13px] font-medium text-destructive-strong">
                {errorMessage}
              </p>
            )
          )}
        </DialogMain>

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() =>
              onSubmit(entries.map((entry) => ({ user_id: entry.userId, amount_usd: entry.amount })))
            }
            disabled={!canSubmit}
          >
            {isSubmitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Users className="h-3.5 w-3.5" aria-hidden />
            )}
            {/* The amount is on the button. It is the last thing read before the card is charged. */}
            {isAssign
              ? isSubmitting
                ? 'Assigning…'
                : total > 0
                  ? `Assign ${formatCurrencyUsd(total)}`
                  : 'Assign'
              : isSubmitting
                ? 'Charging…'
                : total > 0
                  ? `Charge ${formatCurrencyUsd(total)}`
                  : 'Charge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
